import type { SeoLandingConfig } from "../types";

// Fatos com fonte em .claude/skills/seo/reference/fidexa-empresa.md
// (siglas: PEGN, ERP = Portal ERP, NV = Novo Varejo).
// Nunca usar "Pix parcelado" (busca do consumidor) nem "Pix Crediário"
// (nome de produto de terceiro): keywords.md §2.1, linha 3.
const SOURCE_ERP_NV = "Portal ERP e Novo Varejo, ago/2026";

export const pixNoCrediario: SeoLandingConfig = {
  path: "/pix-no-crediario",
  kind: "ferramenta",
  keyword: "receber parcela do crediário por Pix",
  title: "Receber parcela do crediário por Pix no WhatsApp",
  description:
    "Receba a parcela do crediário por Pix dentro da conversa no WhatsApp: o cliente escolhe a parcela, paga na hora e a baixa é automática, sem conferir extrato.",
  breadcrumbLabel: "Pix no crediário",
  updatedAt: "2026-10-17",
  service: {
    name: "Fidexa Pay: parcela do crediário paga por Pix no WhatsApp",
    serviceType:
      "Recebimento de parcelas de crediário próprio por Pix com baixa automática",
    audience: "Lojas e redes do varejo com crediário próprio",
  },
  sections: [
    {
      type: "hero",
      eyebrow: "Fidexa Pay · Pix na cobrança do crediário",
      title: "Receber parcela do crediário por Pix, sem sair do WhatsApp",
      lead: "A mensagem de cobrança chega no WhatsApp, o cliente decide qual parcela quer quitar e paga por Pix ali mesmo. A Fidexa identifica o pagamento e dá a baixa sozinha.",
      ctaLabel: "Agendar demonstração gratuita",
      secondaryLabel: "Ver o pagamento passo a passo",
      secondaryHref: "#como-funciona",
      reassurance:
        "Um especialista mostra o Fidexa Pay na demonstração gratuita.",
    },
    {
      type: "proof",
      title: "Lojistas que recebem o crediário pela Fidexa:",
      clientNames: [
        "Lojas Elder",
        "SuperModas",
        "Lojas Skala",
        "Sete 4 Sete",
        "Lojas Rosário",
        "Tangará",
      ],
    },
    {
      type: "shortAnswer",
      id: "resposta-curta",
      title: "Como a loja recebe a parcela do crediário por Pix",
      answer:
        "Com o Fidexa Pay, a cobrança chega ao cliente pelo WhatsApp, ele escolhe qual parcela do crediário quer pagar e faz o Pix sem sair do chat. Em seguida, a Fidexa reconhece o pagamento e registra a baixa automática da parcela, sem ninguém da loja abrir o extrato. É autoatendimento: o cliente paga sozinho, a qualquer hora.",
      bullets: [
        "Vale para parcela em atraso e para lembrete de parcela que ainda vai vencer.",
        "Lançado em agosto de 2026, em parceria com a TOTVS Linx.",
        "Cobrança enviada pelo número oficial da loja na API do WhatsApp.",
      ],
    },
    {
      type: "steps",
      id: "como-funciona",
      title: "Do aviso no WhatsApp à parcela baixada",
      lead: "O caminho que o cliente percorre para pagar, e o que a loja deixa de fazer.",
      steps: [
        {
          title: "A cobrança chega na conversa",
          text: "A régua automática manda a mensagem no WhatsApp com o que está em aberto no crediário, seja um lembrete de parcela futura, seja um aviso de atraso.",
        },
        {
          title: "O cliente escolhe a parcela",
          text: "Ele vê as parcelas em aberto e decide qual quer quitar agora, sem ligar para a loja nem esperar alguém do crediário responder.",
        },
        {
          title: "Paga por Pix ali mesmo",
          text: "O Pix é feito sem sair do WhatsApp, a qualquer hora, mesmo com a loja fechada. Quem prefere negociar o atraso segue falando com os vendedores ou o crediário da loja, pelo chat da Fidexa.",
        },
        {
          title: "A baixa acontece sozinha",
          text: "A Fidexa reconhece o Pix e registra o pagamento da parcela. Ninguém precisa conferir o crédito na conta da loja e lançar a baixa à mão.",
        },
      ],
    },
    {
      type: "comparison",
      id: "comparativo-pix",
      title:
        "Chave Pix, boleto ou Fidexa Pay: quem trabalha depois do pagamento",
      lead: "Receber o dinheiro é metade do caminho. A outra metade é saber de qual parcela ele é.",
      caption:
        "Comparação entre receber a parcela do crediário por chave Pix da loja, por boleto ou link enviado à mão e pelo Fidexa Pay no WhatsApp",
      columns: [
        { key: "chave", label: "Chave Pix da loja" },
        { key: "boleto", label: "Boleto ou link enviado à mão" },
        { key: "fidexa", label: "Fidexa Pay", highlight: true },
      ],
      rows: [
        {
          label: "Cliente paga sem sair do WhatsApp",
          cells: { chave: false, boleto: false, fidexa: true },
        },
        {
          label: "Cliente escolhe qual parcela quitar",
          cells: {
            chave: "Precisa perguntar à loja",
            boleto: "Só a que foi enviada",
            fidexa: "Na própria conversa",
          },
        },
        {
          label: "Quem identifica o pagamento",
          cells: {
            chave: "Alguém olhando o extrato",
            boleto: "Alguém da loja",
            fidexa: "A Fidexa",
          },
        },
        {
          label: "Baixa da parcela no crediário",
          cells: {
            chave: "Lançada à mão",
            boleto: "Depende de conferência",
            fidexa: "Automática",
          },
        },
        {
          label: "Negociação do atraso",
          cells: {
            chave: "Celular de quem cobra",
            boleto: "Por telefone",
            fidexa: "Equipe da loja, no chat da Fidexa",
          },
        },
        {
          label: "Cliente paga com a loja fechada",
          cells: {
            chave: "Paga, mas a baixa espera",
            boleto: "Paga, mas a baixa espera",
            fidexa: "24 horas, com baixa automática",
          },
        },
      ],
    },
    {
      type: "metrics",
      id: "numeros-recebimento",
      title: "O que já passou pela cobrança da Fidexa",
      metrics: [
        {
          value: "R$ 34 mi",
          label: "Em parcelas e recebíveis recuperados, fora os juros",
          source: SOURCE_ERP_NV,
        },
        {
          value: "480.345",
          label: "Acordos de pagamento fechados",
          source: "Plataforma Fidexa",
        },
        {
          value: "32,10%",
          label: "Taxa média de recuperação da carteira",
          source: "Plataforma Fidexa",
        },
      ],
    },
    {
      type: "faq",
      id: "perguntas-pix",
      title: "Perguntas sobre receber parcela do crediário por Pix",
      items: [
        {
          question: "Como receber parcela do crediário por Pix?",
          answer:
            "Pelo Fidexa Pay: a loja envia a cobrança no WhatsApp, o cliente indica qual parcela quer quitar e faz o Pix no mesmo chat. O pagamento é identificado e a parcela, baixada, então o lojista não precisa receber comprovante nem conferir o extrato.",
        },
        {
          question: "O que é baixa automática de parcela?",
          answer:
            "É o registro do pagamento feito sem intervenção humana. Antes do Fidexa Pay, alguém ainda precisava verificar o crédito na conta e dar baixa no sistema de gestão. Agora, quando o Pix é feito no chat, a Fidexa baixa a parcela e computa o pagamento.",
        },
        {
          question: "O cliente pode escolher qual parcela vai pagar?",
          answer:
            "Sim. O Fidexa Pay funciona como autoatendimento: na conversa o cliente vê o que está em aberto e escolhe a parcela que quer quitar, sem precisar falar com alguém do crediário.",
        },
        {
          question: "Dá para pagar por Pix uma parcela que ainda não venceu?",
          answer:
            "Sim. A régua envia lembretes das parcelas que ainda vão vencer, e o cliente pode quitar por Pix pela própria mensagem, antes do vencimento.",
        },
        {
          question: "E se o cliente atrasou e não consegue pagar tudo?",
          answer:
            "A negociação fica com a equipe da loja: o cliente conversa com ela no chat da Fidexa e os dois combinam o acordo de parcelamento. Quem só quer pagar uma parcela resolve sozinho pelo Fidexa Pay.",
        },
        {
          question: "O que é o Fidexa Pay?",
          answer:
            "É a infraestrutura de gestão de recebíveis da Fidexa, lançada em agosto de 2026 em parceria com a TOTVS Linx. Ela levou a Fidexa de um sistema de disparo de mensagens para o ciclo completo: cobrança, pagamento por Pix e baixa da parcela.",
        },
        {
          question: "Preciso usar um ERP específico para receber por Pix?",
          answer:
            "Foi desenhado inicialmente para lojas que usam o ERP TOTVS Linx, e a integração com outros ERPs está em andamento. Um especialista confirma, na demonstração, como fica a operação com o sistema que a sua loja usa hoje.",
        },
        {
          question:
            "Cobrar por Pix no WhatsApp pode bloquear o número da loja?",
          answer:
            "O risco cai porque a Fidexa não usa o aplicativo comum do celular: as cobranças trafegam pela API oficial do WhatsApp, no modelo de template que a Meta aprova.",
        },
        {
          question: "Vale a pena cobrar por Pix parcelas de valor baixo?",
          answer:
            "Sim. A Fidexa cobra dívida de qualquer valor, até parcelas de R$ 50 que muita loja deixa para lá. Como o pagamento e a baixa não dependem de ninguém da loja, parcelas pequenas deixam de ficar esquecidas por falta de tempo.",
        },
        {
          question: "Quanto custa o Fidexa Pay?",
          answer:
            "O valor depende do tamanho da operação. Um especialista apresenta o Fidexa Pay numa demonstração gratuita, já com a carteira real da loja, e envia a proposta em seguida.",
        },
      ],
    },
    {
      type: "finalCta",
      id: "cta-final-pix",
      title: "Pare de conferir extrato para dar baixa em parcela",
      text: "Veja o Pix do crediário caindo e a parcela baixando sozinha, numa demonstração gratuita guiada por um especialista.",
      ctaLabel: "Agendar demonstração gratuita",
    },
  ],
};
